import { supabase } from "@/lib/supabase";
import type { ActivityBadge, ActivityBadgeType, BadgeChange, BadgeTier } from "@/lib/activityBadges";
import type { IActivityBadgeService } from "@/services/interfaces/IActivityBadgeService";

interface BadgeRow {
  badge_type: ActivityBadgeType;
  tier: BadgeTier | null;
  earned_at: string;
}

function toActivityBadge(row: BadgeRow): ActivityBadge {
  return { badgeType: row.badge_type, tier: row.tier, earnedAt: row.earned_at };
}

export class SupabaseActivityBadgeService implements IActivityBadgeService {
  async refresh(): Promise<BadgeChange[]> {
    const { data, error } = await supabase.rpc("refresh_activity_badges");
    if (error) throw new Error(error.message);
    const changes = data as unknown as { badge_type: string; tier: string | null; action: string }[];
    return (changes ?? [])
      .filter((c) => c.action)
      .map((c) => ({
        badgeType: c.badge_type as ActivityBadgeType,
        tier: (c.tier as BadgeTier) ?? null,
        action: c.action as "earned" | "upgraded",
      }));
  }

  async listForUser(userId: string): Promise<ActivityBadge[]> {
    const { data, error } = await supabase
      .from("activity_badges")
      .select("badge_type, tier, earned_at")
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    return ((data ?? []) as unknown as BadgeRow[]).map(toActivityBadge);
  }

  async listForHandle(handle: string, profileId: string): Promise<ActivityBadge[]> {
    const { data, error } = await supabase.rpc("get_activity_badges_for_handle", {
      p_handle: handle,
      p_profile_id: profileId,
    });
    if (error) throw new Error(error.message);
    const rows = data as unknown as BadgeRow[];
    return (rows ?? []).map(toActivityBadge);
  }
}
